// src/pages/TrialEnroll.js
// Parent-facing trial conversion page at /enroll.
//
// Standalone authed page (its own onAuthStateChanged, like TrialStatus) — NOT
// wrapped in ProtectedRoute. ProtectedRoute's trial guard bounces a parent
// whose children are all course:null to /trial-status, which would make this
// page unreachable for exactly the people it's for. Reachable for ANY signed-in
// user with a courseless trial student, WHATEVER the trial's status — payment
// is never gated on trial status.
//
// Nothing is re-entered here: name and date of birth are read-only, straight
// from the child / trial record. The parent picks a package per student, ticks
// who to enrol now, optionally adds a discount code, and pays. On payment the
// createTrialConversionCheckout callable + the trial_conversion webhook branch
// attach a course to the EXISTING child record and mark the trial `won`.
//
// Feature-flagged: this route is only registered when TRIAL_SIGNUP_ENABLED.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Crown, Sparkles, Home, Tag, CheckCircle, XCircle, Loader2, ShieldCheck, ArrowLeft,
} from 'lucide-react';
import Footer from '../components/Footer';
import PackageCard from '../components/PackageCard';
import { COURSE_PACKAGES, getSortedPackages } from '../config/coursePackages';
import { stripePromise, validateDiscountCode } from '../config/stripe';
import { getChildren, calculateAge, isAdultAccount } from '../utils/childrenHelper';
import { TRIAL_STATUS_LABELS } from '../utils/trialHelper';
import { resolveDiscount } from '../utils/discountPricing';
import { stashEnrolReturn, clearEnrolReturn } from '../utils/pendingEnrol';

// Matches the server guard in createTrialConversionCheckout.
const ADULT_PACKAGE_ID = 'course_6week_adult_group';
const CHILD_PACKAGES = getSortedPackages().filter((p) => p.audience !== 'adult');

function ageLabel(dob) {
  if (!dob) return null;
  const age = calculateAge(dob);
  return age != null ? `Age ${age}` : null;
}

function TrialEnroll() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canceled = searchParams.get('canceled') === 'true';

  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [profile, setProfile] = useState(null);
  const [rows, setRows] = useState([]); // { trialId, studentId, name, ageStr, status }
  const [selected, setSelected] = useState({});        // trialId -> bool
  const [packageByTrial, setPackageByTrial] = useState({}); // trialId -> packageId

  const [discountCode, setDiscountCode] = useState('');
  const [discountValidation, setDiscountValidation] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  const [savePaymentMethod, setSavePaymentMethod] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isAdult = isAdultAccount(profile);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        // Come back here after sign-in rather than the default landing —
        // Login honours this marker after its own branch ladder.
        stashEnrolReturn();
        navigate('/login', { replace: true });
        return;
      }
      clearEnrolReturn(); // made it — drop any stale marker
      setUser(u);
      setAuthReady(true);
    });
    return () => unsub();
  }, [navigate]);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(false);
    try {
      const [profileSnap, trialSnap] = await Promise.all([
        getDoc(doc(db, 'users', user.uid)),
        getDocs(query(collection(db, 'trials'), where('userId', '==', user.uid))),
      ]);

      const prof = profileSnap.exists() ? profileSnap.data() : null;
      setProfile(prof);

      const children = getChildren(prof);
      const childById = new Map(
        children.filter((c) => c && c.id).map((c) => [c.id, c]),
      );
      const adultAcct = isAdultAccount(prof);
      const defaultPkg = adultAcct ? ADULT_PACKAGE_ID : 'course_6week_group';

      const trials = trialSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')));

      const nextRows = [];
      const nextSelected = {};
      const nextPackages = {};
      for (const t of trials) {
        // NOTE: no `convertedAt` skip. convertedAt is set by TrialManager's
        // outcome path with no payment and no course, so it isn't a reliable
        // "already enrolled" signal. child.course (below) is — it's what
        // ProtectedRoute, the renewal paths and the conversion webhook all use.
        const child = t.studentId ? childById.get(t.studentId) : null;
        if (!child) continue;                        // not linked to a child on this account
        if (child.course) continue;                  // already enrolled
        nextRows.push({
          trialId: t.id,
          studentId: t.studentId,
          name: child.name || t.studentName || 'Student',
          ageStr: ageLabel(child.dateOfBirth || t.dateOfBirth),
          status: t.status || 'requested',
        });
        nextSelected[t.id] = true;
        nextPackages[t.id] = defaultPkg;
      }

      setRows(nextRows);
      setSelected(nextSelected);
      setPackageByTrial(nextPackages);
    } catch (err) {
      console.error('TrialEnroll: load failed', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const chosen = useMemo(
    () => rows.filter((r) => selected[r.trialId]),
    [rows, selected],
  );

  const subtotal = useMemo(
    () => chosen.reduce((sum, r) => {
      const pkg = COURSE_PACKAGES[packageByTrial[r.trialId]];
      return sum + (pkg ? pkg.price : 0);
    }, 0),
    [chosen, packageByTrial],
  );

  // Recompute against the real (possibly mixed-package) basket — see resolveDiscount.
  const discount = useMemo(
    () => resolveDiscount(discountValidation?.valid ? discountValidation.coupon : null, subtotal),
    [discountValidation, subtotal],
  );
  const discountApplies = !!discountValidation?.valid && !discount.wrongCurrency && discount.amount > 0;
  const discountAmount = discountApplies ? discount.amount : 0;
  const total = Math.max(0, subtotal - discountAmount);

  const toggleRow = (trialId) => {
    setSelected((s) => ({ ...s, [trialId]: !s[trialId] }));
    setDiscountValidation(null); // package mix / count changed
  };

  const setRowPackage = (trialId, packageId) => {
    setPackageByTrial((p) => ({ ...p, [trialId]: packageId }));
    setDiscountValidation(null);
  };

  const handleValidateDiscount = async () => {
    const code = discountCode.trim();
    if (!code) { setDiscountValidation(null); return; }
    if (chosen.length === 0) { setError('Pick at least one student first.'); return; }
    setError(null);
    setValidatingDiscount(true);
    try {
      // Validate + get the coupon back only — resolveDiscount prices the real
      // basket, so this call's own discountAmount/finalAmount are unused. The
      // real basket subtotal (cents) IS sent so the server can check a code's
      // minimum_amount against a mixed-package basket.
      const res = await validateDiscountCode(
        code, packageByTrial[chosen[0].trialId], 1, Math.round(subtotal * 100),
      );
      setDiscountValidation(res);
    } catch (err) {
      setDiscountValidation({ valid: false, error: err.message || 'Could not check that code.' });
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    const items = chosen.map((r) => ({ trialId: r.trialId, packageId: packageByTrial[r.trialId] }));
    if (items.length === 0) {
      setError('Select at least one student to enrol.');
      return;
    }
    setSubmitting(true);
    try {
      const call = httpsCallable(getFunctions(), 'createTrialConversionCheckout');
      const res = await call({
        items,
        // Only send a code we've validated AND can price exactly — otherwise the
        // parent could be charged a figure different from the one on this page.
        discountCode: discountApplies ? discountCode.trim() : null,
        savePaymentMethod,
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}&conversion=true`,
        cancelUrl: `${window.location.origin}/enroll?canceled=true`,
      });
      const sessionId = res && res.data && res.data.sessionId;
      if (!sessionId) throw new Error('Could not start checkout. Please try again.');
      const stripe = await stripePromise;
      const { error: stripeErr } = await stripe.redirectToCheckout({ sessionId });
      if (stripeErr) throw stripeErr;
      // On success the browser has already left for Stripe — keep the spinner.
    } catch (err) {
      console.error('TrialEnroll: checkout failed', err);
      // A promotion-code restriction failure from the server (Stripe refused the
      // code) belongs next to the coupon field, not as a page-level error.
      if (err && err.details && err.details.discountError) {
        setDiscountValidation({ valid: false, error: err.message });
      } else {
        setError(err.message || 'Something went wrong starting checkout. Please try again.');
      }
      setSubmitting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (!authReady || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <div className="text-purple-600 text-xl font-bold">Loading your trials…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <div className="container mx-auto px-4 py-10 max-w-2xl">
        <button
          onClick={() => navigate('/trial-status')}
          className="inline-flex items-center gap-2 text-purple-200 hover:text-white text-sm font-medium transition-colors mb-4"
        >
          <ArrowLeft size={15} /> Back to trial status
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 mb-4">
            <Sparkles className="text-yellow-400" size={20} />
            <span className="text-white font-bold">My Roots</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2">Enrol &amp; get started</h1>
          <p className="text-purple-100">
            Pick a course for each student. You can enrol before or after the trial —
            whenever you&apos;re ready.
          </p>
        </div>

        {canceled && (
          <div className="bg-white rounded-2xl p-4 shadow-xl border-2 border-yellow-200 mb-4 flex items-start gap-3">
            <div className="text-yellow-500 text-xl">⚠️</div>
            <div className="text-sm text-gray-700">
              Payment was canceled — nothing was charged. You can try again below.
            </div>
          </div>
        )}

        {loadError ? (
          <div className="bg-white rounded-2xl p-8 shadow-xl text-center">
            <div className="text-4xl mb-3">😕</div>
            <div className="text-lg font-bold text-gray-800 mb-2">We couldn&apos;t load your trials</div>
            <p className="text-gray-600 mb-6">Please refresh the page, or try again in a moment.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors"
            >
              Refresh
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 shadow-xl text-center">
            <Crown className="text-purple-400 mx-auto mb-3" size={36} />
            <div className="text-lg font-bold text-gray-800 mb-2">Nothing to enrol right now</div>
            <p className="text-gray-600 mb-6">
              You don&apos;t have any trial students waiting to be enrolled. If you think this
              is wrong, email us at{' '}
              <a href="mailto:info@my-roots.ca" className="text-purple-600 underline font-medium">info@my-roots.ca</a>.
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors"
            >
              <Home size={16} /> Back to Home
            </button>
          </div>
        ) : (
          <>
            {/* Student rows */}
            <div className="space-y-4">
              {rows.map((r) => {
                const isOn = !!selected[r.trialId];
                const pkgId = packageByTrial[r.trialId];
                return (
                  <div
                    key={r.trialId}
                    className={`bg-white rounded-2xl p-5 shadow-xl border ${
                      isOn ? 'border-purple-200' : 'border-gray-200 opacity-70'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={() => toggleRow(r.trialId)}
                        className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                        aria-label={`Enrol ${r.name}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <div className="text-lg font-bold text-gray-800">{r.name}</div>
                            <div className="text-sm text-gray-500">
                              {[r.ageStr, isAdult ? 'Adult learner' : null].filter(Boolean).join(' · ') || ' '}
                            </div>
                          </div>
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
                            Trial: {TRIAL_STATUS_LABELS[r.status] || r.status}
                          </span>
                        </div>

                        <div className="mt-3">
                          <label className="block text-xs font-semibold text-gray-600 mb-2">
                            {isAdult ? 'Your course' : 'Choose a course'}
                          </label>
                          {isAdult ? (
                            <PackageCard pkg={COURSE_PACKAGES[ADULT_PACKAGE_ID]} selected />
                          ) : (
                            <div className="space-y-3">
                              {CHILD_PACKAGES.map((p) => (
                                <PackageCard
                                  key={p.id}
                                  pkg={p}
                                  selected={pkgId === p.id}
                                  onSelect={isOn ? (id) => setRowPackage(r.trialId, id) : undefined}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Discount code */}
            <div className="bg-white rounded-2xl p-5 shadow-xl border border-purple-100 mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Discount code <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountValidation(null); }}
                    placeholder="Enter code"
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleValidateDiscount}
                  disabled={!discountCode.trim() || validatingDiscount}
                  className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl text-sm disabled:opacity-50 transition-colors"
                >
                  {validatingDiscount ? <Loader2 className="animate-spin" size={16} /> : 'Apply'}
                </button>
              </div>
              {discountValidation && (
                <div className={`mt-2 flex items-center gap-2 text-sm ${discountApplies ? 'text-green-600' : 'text-red-600'}`}>
                  {discountApplies ? <CheckCircle size={15} /> : <XCircle size={15} />}
                  {!discountValidation.valid
                    ? (discountValidation.error || 'That code is not valid.')
                    : discount.wrongCurrency
                      ? "This code can't be applied to your order."
                      : discountApplies
                        ? `Applied — you save $${discountAmount.toFixed(2)}`
                        : 'This code has no effect on your order.'}
                </div>
              )}
            </div>

            {/* Save card */}
            <label className="flex items-start gap-3 bg-white rounded-2xl p-4 shadow-xl border border-gray-100 mt-4 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={savePaymentMethod}
                onChange={(e) => setSavePaymentMethod(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-semibold">Save my card for easy renewal.</span>{' '}
                <span className="text-gray-500 text-xs">
                  Stored securely by Stripe. Remove it any time in your account settings.
                </span>
              </span>
            </label>

            {/* Summary + pay */}
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-purple-100 mt-4">
              <div className="space-y-1.5 text-sm">
                {chosen.map((r) => {
                  const pkg = COURSE_PACKAGES[packageByTrial[r.trialId]];
                  return (
                    <div key={r.trialId} className="flex justify-between text-gray-700">
                      <span>{r.name} — {pkg?.shortLabel || pkg?.name || 'Course'}</span>
                      <span>${(pkg?.price || 0).toFixed(2)}</span>
                    </div>
                  );
                })}
                {chosen.length === 0 && (
                  <div className="text-gray-400">No students selected.</div>
                )}
              </div>

              <div className="border-t border-gray-100 mt-3 pt-3 space-y-1.5 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)} CAD</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>−${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold text-gray-900 pt-1">
                  <span>Total</span>
                  <span className="text-purple-600">${total.toFixed(2)} CAD</span>
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 font-medium">
                  {error}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={submitting || chosen.length === 0}
                className="w-full mt-4 px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
              >
                {submitting ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Starting checkout…
                  </>
                ) : (
                  <>
                    <ShieldCheck size={20} />
                    Pay ${total.toFixed(2)} CAD
                  </>
                )}
              </button>
              <p className="text-center text-xs text-gray-400 mt-3">
                Secure checkout with Stripe. Your course starts 7 days after payment.
              </p>
            </div>
          </>
        )}
      </div>
      <Footer variant="minimal" />
    </div>
  );
}

export default TrialEnroll;
